import { useState } from "react";
import { ALL, ratingOptions, sortOptions, yearOptions, type FilterState } from "@/utils/catalog";
import { regions } from "@/mocks/categories";

interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
}

const chipBase =
  "cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors duration-200";
const chipActive = "border-primary-800 bg-primary-900 text-background-50";
const chipIdle = "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400";

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={`${chipBase} ${active ? chipActive : chipIdle}`}>
      {children}
    </button>
  );
}

function FilterPanel({ value, onChange }: FilterBarProps) {
  return (
    <div className="space-y-5">
      <div>
        <p className="rule-label mb-2 text-foreground-500">地区</p>
        <div className="flex flex-wrap gap-2">
          {[ALL, ...regions].map((region) => (
            <Chip
              key={region}
              active={value.region === region}
              onClick={() => onChange({ ...value, region })}
            >
              {region}
            </Chip>
          ))}
        </div>
      </div>
      <div>
        <p className="rule-label mb-2 text-foreground-500">年份</p>
        <div className="flex flex-wrap gap-2">
          {yearOptions.map((option) => (
            <Chip
              key={option.value}
              active={value.yearRange === option.value}
              onClick={() => onChange({ ...value, yearRange: option.value })}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      </div>
      <div>
        <p className="rule-label mb-2 text-foreground-500">评分</p>
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map((option) => (
            <Chip
              key={option.value}
              active={value.ratingRange === option.value}
              onClick={() => onChange({ ...value, ratingRange: option.value })}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FilterBar({ value, onChange }: FilterBarProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const activeCount = [value.region, value.yearRange, value.ratingRange].filter((item) => item !== ALL).length;

  const reset = () => onChange({ ...value, region: ALL, yearRange: ALL, ratingRange: ALL });

  return (
    <>
      <div className="hidden rounded-lg border border-background-200 bg-background-50 p-5 lg:block">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <FilterPanel value={value} onChange={onChange} />
          </div>
          <div className="w-48 shrink-0 border-l border-background-200 pl-5">
            <p className="rule-label mb-2 text-foreground-500">排序</p>
            <div className="flex flex-col gap-1.5">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange({ ...value, sort: option.value })}
                  className={`flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-left text-xs transition-colors ${
                    value.sort === option.value
                      ? "bg-secondary-100 font-medium text-secondary-900"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {option.label}
                  {value.sort === option.value ? <i className="ri-check-line text-accent-600" /> : null}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={reset}
              className="mt-4 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 lg:hidden">
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800"
        >
          <i className="ri-filter-3-line" />
          筛选{activeCount > 0 ? ` (${activeCount})` : ""}
        </button>
        <label className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
          <i className="ri-sort-desc text-foreground-500" />
          <select
            aria-label="排序方式"
            value={value.sort}
            onChange={(event) => onChange({ ...value, sort: event.target.value as FilterState["sort"] })}
            className="cursor-pointer bg-transparent text-sm text-foreground-800 outline-none"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {panelOpen ? (
        <div className="fixed inset-0 z-[70] flex flex-col bg-background-50 animate-fade-in lg:hidden">
          <div className="container-page flex h-16 items-center justify-between border-b border-background-200">
            <span className="font-heading text-base font-semibold text-foreground-950">筛选条件</span>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setPanelOpen(false)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="container-page flex-1 overflow-y-auto py-6">
            <FilterPanel value={value} onChange={onChange} />
            <div className="mt-8">
              <p className="rule-label mb-2 text-foreground-500">排序</p>
              <div className="flex flex-col gap-1.5">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange({ ...value, sort: option.value })}
                    className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-sm ${
                      value.sort === option.value
                        ? "bg-secondary-100 font-medium text-secondary-900"
                        : "text-foreground-600"
                    }`}
                  >
                    {option.label}
                    {value.sort === option.value ? <i className="ri-check-line text-accent-600" /> : null}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="container-page flex gap-3 border-t border-background-200 py-4">
            <button
              type="button"
              onClick={reset}
              className="flex-1 cursor-pointer rounded-md border border-background-300 px-4 py-3 text-sm text-foreground-800"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="flex-1 cursor-pointer rounded-md bg-primary-900 px-4 py-3 text-sm font-medium text-background-50"
            >
              查看结果
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}